import { useEffect, useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type ModalType = 'shop' | 'levels' | 'settings' | 'profile' | 'customize' | null;

interface UseModalRouterReturn {
  activeModal: ModalType;
  openModal: (modal: ModalType) => void;
  closeModal: () => void;
  isModalOpen: (modal: ModalType) => boolean;
}

/**
 * Hook for managing modal state through URL parameters
 * Provides pseudo-routing where modals update the URL without navigation
 */
export function useModalRouter(): UseModalRouterReturn {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState<ModalType>(null);

  // Parse modal from URL on mount and when location changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const modal = params.get('modal') as ModalType;
    setActiveModal(modal);
  }, [location.search]);

  // Open a modal by updating URL
  const openModal = useCallback((modal: ModalType) => {
    if (!modal) return;
    
    const params = new URLSearchParams(location.search);
    params.set('modal', modal);
    
    // Update URL without navigation
    navigate(`${location.pathname}?${params.toString()}`, { replace: false });
  }, [location.pathname, location.search, navigate]);

  // Close modal by removing modal param from URL
  const closeModal = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('modal');
    
    const newSearch = params.toString();
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
    
    // Update URL without navigation
    navigate(newUrl, { replace: false });
  }, [location.pathname, location.search, navigate]);

  // Check if a specific modal is open
  const isModalOpen = useCallback((modal: ModalType) => {
    return activeModal === modal;
  }, [activeModal]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const modal = params.get('modal') as ModalType;
      setActiveModal(modal);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return {
    activeModal,
    openModal,
    closeModal,
    isModalOpen,
  };
}